/**
 * KaraFun WebSocket Handler - Popup Window Version
 * Manages WebSocket connection to KaraFun via popup window
 */
import { useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const KaraFunWebSocketHandler = ({ isActive, onStatusChange }) => {
  const [isConnected, setIsConnected] = useState(false);
  const messageIdRef = useRef(0);
  const pollingIntervalRef = useRef(null);
  const popupRef = useRef(null);

  // Get next message ID
  const getNextMessageId = () => {
    messageIdRef.current += 1;
    return messageIdRef.current;
  };

  // Open popup window
  const openKaraFunPopup = () => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return popupRef.current;
    }

    const width = 1200;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const popup = window.open(
      'https://www.karafun.com/095008',
      'KaraFunPlayer',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (popup) {
      popupRef.current = popup;
      console.log('✅ KaraFun popup opened');
      
      // Inject script after popup loads
      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          console.log('❌ KaraFun popup closed');
          setIsConnected(false);
          onStatusChange?.(false, 'Popup closed');
          popupRef.current = null;
        } else {
          try {
            // Try to inject script
            if (popup.document && popup.document.readyState === 'complete') {
              injectWebSocketInterceptor(popup);
              clearInterval(checkPopup);
            }
          } catch (e) {
            // Cross-origin - can't access yet, keep trying
          }
        }
      }, 500);
    } else {
      console.error('❌ Failed to open popup - check popup blocker');
      onStatusChange?.(false, 'Failed to open popup');
    }

    return popup;
  };

  // Inject WebSocket interceptor into popup
  const injectWebSocketInterceptor = (popup) => {
    try {
      const script = popup.document.createElement('script');
      script.textContent = `
        (function() {
          if (window.karafunInterceptorInstalled) return;
          window.karafunInterceptorInstalled = true;
          
          console.log('🔧 Installing WebSocket interceptor...');
          const OriginalWebSocket = window.WebSocket;
          
          window.WebSocket = function(url, protocols) {
            console.log('📡 WebSocket created:', url);
            const ws = new OriginalWebSocket(url, protocols);
            
            window.karafunWS = ws;
            
            ws.addEventListener('open', () => {
              console.log('✅ KaraFun WebSocket opened');
              window.opener.postMessage({ type: 'KARAFUN_WS_CONNECTED' }, '*');
            });
            
            ws.addEventListener('message', (event) => {
              window.opener.postMessage({ 
                type: 'KARAFUN_WS_MESSAGE', 
                data: event.data 
              }, '*');
            });
            
            ws.addEventListener('close', () => {
              console.log('❌ KaraFun WebSocket closed');
              window.opener.postMessage({ type: 'KARAFUN_WS_DISCONNECTED' }, '*');
            });
            
            return ws;
          };
          
          // Listen for commands from parent
          window.addEventListener('message', (event) => {
            if (event.data.type === 'SEND_TO_KARAFUN' && event.data.message && window.karafunWS) {
              const msg = JSON.stringify(event.data.message);
              console.log('📤 Sending to KaraFun:', msg);
              window.karafunWS.send(msg);
            }
          });
          
          console.log('✅ WebSocket interceptor installed');
        })();
      `;
      
      popup.document.head.appendChild(script);
      console.log('✅ Script injected into popup');
    } catch (err) {
      console.error('❌ Failed to inject script:', err);
    }
  };

  // Send message to KaraFun via popup
  const sendToKaraFun = (message) => {
    try {
      if (!popupRef.current || popupRef.current.closed) {
        console.error('KaraFun popup not open');
        return false;
      }

      popupRef.current.postMessage({
        type: 'SEND_TO_KARAFUN',
        message: message
      }, '*');
      
      console.log('📤 Sent to KaraFun:', message.type);
      return true;
    } catch (err) {
      console.error('Error sending to KaraFun:', err);
      return false;
    }
  };

  // Poll for pending commands from backend
  const pollCommands = async () => {
    try {
      const response = await fetch(`${API}/api/karafun/commands/pending`);
      if (!response.ok) return;
      
      const commands = await response.json();
      
      for (const command of commands) {
        console.log('📝 Executing command:', command.type, command);
        
        let result = false;
        
        switch (command.type) {
          case 'add_song':
            result = addSong(command.song_id, command.singer_name);
            break;
          case 'play':
            result = play();
            break;
          case 'pause':
            result = pause();
            break;
          // Skip removed - causes disconnections
          case 'restart':
            result = restart();
            break;
          default:
            console.warn('Unknown command type:', command.type);
        }
        
        await fetch(`${API}/api/karafun/commands/${command.id}/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ success: result })
        });
      }
    } catch (err) {
      console.error('Error polling commands:', err);
    }
  };

  // KaraFun command implementations
  const addSong = (songId, singerName) => {
    const message = {
      id: getNextMessageId(),
      type: 'remote.AddToQueueRequest',
      payload: {
        song: {
          type: 1,
          id: songId
        },
        options: {
          singer: singerName
        }
      }
    };
    
    console.log('🎵 Adding song:', songId, 'for', singerName);
    return sendToKaraFun(message);
  };

  const play = () => {
    const message = {
      id: getNextMessageId(),
      type: 'remote.PlayRequest',
      payload: {}
    };
    
    console.log('▶️ Play');
    return sendToKaraFun(message);
  };

  const pause = () => {
    const message = {
      id: getNextMessageId(),
      type: 'remote.PauseRequest',
      payload: {}
    };
    
    console.log('⏸️ Pause');
    return sendToKaraFun(message);
  };

  // Skip function removed - causes WebSocket disconnections in production

  const restart = () => {
    const message = {
      id: getNextMessageId(),
      type: 'remote.RestartRequest',
      payload: {}
    };
    
    console.log('🔄 Restart');
    return sendToKaraFun(message);
  };

  // Listen for messages from popup
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'KARAFUN_WS_CONNECTED') {
        console.log('✅ KaraFun WebSocket connected');
        setIsConnected(true);
        onStatusChange?.(true, null);
      } else if (event.data.type === 'KARAFUN_WS_DISCONNECTED') {
        console.log('❌ KaraFun WebSocket disconnected');
        setIsConnected(false);
        onStatusChange?.(false, 'Disconnected');
      } else if (event.data.type === 'KARAFUN_WS_MESSAGE') {
        try {
          const message = JSON.parse(event.data.data);
          
          if (message.type === 'StatusChangedEvent' && 
              message.payload?.status?.state === 'stopped') {
            fetch(`${API}/api/karafun/song-complete`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' }
            }).catch(err => console.error('Error notifying song complete:', err));
          }
        } catch (err) {
          // Not JSON
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onStatusChange]);

  // Open popup when activated
  useEffect(() => {
    if (isActive) {
      console.log('🚀 Opening KaraFun popup...');
      openKaraFunPopup();
    }
    
    return () => {
      // Don't close popup on cleanup - let user close it
    };
  }, [isActive]);

  // Start/stop command polling
  useEffect(() => {
    if (isActive && isConnected) {
      console.log('🔄 Starting command polling...');
      
      pollCommands();
      pollingIntervalRef.current = setInterval(pollCommands, 2000);
      
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          console.log('⏹️ Stopped command polling');
        }
      };
    }
  }, [isActive, isConnected]);

  return null;
};

export default KaraFunWebSocketHandler;
